  
import Config from '../models/Config';
import { FEATURE_FLAGS_RANGE, FEATURE_FLAGS_WORKSHEET, FeatureFlagRow } from '../models/FeatureFlags';
import { RELEASE_NOTES_RANGE, RELEASE_NOTES_WORKSHEET, ReleaseNoteRow } from '../models/ReleaseHighlights';
import firebase from 'firebase/compat/app';
import { FirebaseAuthService } from './FirebaseAuthService';
// Standings model
export interface StandingRow {
  rowIndexId: number;
  className: string;
  riderName: string;
  horseName: string;
  points: number;
}

export interface SheetRow {
  rowIndex: number;
  value: string;
}

export enum SheetsErrorType {
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  PERMISSION_ERROR = 'PERMISSION_ERROR',
  SHEET_NOT_FOUND = 'SHEET_NOT_FOUND',
  NETWORK_ERROR = 'NETWORK_ERROR',
  RATE_LIMIT_ERROR = 'RATE_LIMIT_ERROR',
  INVALID_RANGE = 'INVALID_RANGE',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export class SheetsError extends Error {
  constructor(public type: SheetsErrorType, message: string, public originalError?: any) {
    super(message);
    this.name = 'SheetsError';
  }
}

export interface Show {
  rowIndexId: number;
  showName: string;
  showDateTime: string;
  streetAddress: string;
  cityState: string;
  addedMoney: string;
  canceled: boolean;
  showId: string; // GUID
  showDetails: string;
}

export class GoogleSheetsService {
  async getFeatureFlagRows(): Promise<FeatureFlagRow[]> {
    const response = await this.callSheetsApi({
      worksheet: FEATURE_FLAGS_WORKSHEET,
      range: FEATURE_FLAGS_RANGE,
      usePublicSheet: true,
    });
    const values: string[][] = response.values || [];
    return values.map((row) => ({
      key: row[0] || '',
      environment: row[1] || '',
      description: row[2] || '',
      notes: row[3] || '',
    }));
  }

  async getReleaseNoteRows(): Promise<ReleaseNoteRow[]> {
    const response = await this.callSheetsApi({
      worksheet: RELEASE_NOTES_WORKSHEET,
      range: RELEASE_NOTES_RANGE,
      usePublicSheet: true,
    });
    const values: string[][] = response.values || [];
    return values.map((row) => ({
      version: row[0] || '',
      releasedAt: row[1] || '',
      title: row[2] || '',
      summary: row[3] || '',
    }));
  }

  /**
   * Update a show result row by index in the Show Results sheet
   * Columns: A Division, B Rider Name, C Horse Name, D Time, E ShowId, F Show Name
   * rowIndex is 1-based (A2 is 1)
   */
  async updateShowResultByIndex(rowIndex: number, result: {
    division: string;
    riderName: string;
    horseName: string;
    time: string;
    showId: string;
    showName: string;
  }): Promise<void> {
    const worksheet = 'Show Results';
    // A2 is index 1, so rowIndex+1 for Sheets API
    const range = `A${rowIndex+1}:F${rowIndex+1}`;
    const values = [[
      result.division,
      result.riderName,
      result.horseName,
      result.time,
      result.showId,
      result.showName
    ]];
    await this.callSheetsApi({
      worksheet,
      range,
      method: 'PUT',
      body: { values },
      valueInputOption: 'USER_ENTERED'
    });
  }
  /**
   * Checks if the current user has edit rights on the main editable sheet by attempting a harmless write (read-modify-write of a cell in the Shows sheet).
   * Returns true if successful, false if permission denied.
   */
  async hasEditRightsOnEditableSheet(): Promise<boolean> {
    const worksheet = 'Shows';
    const range = 'A1';
    try {
      // Read the current value
      const response = await this.callSheetsApi({ worksheet, range });
      const currentValue = response.values?.[0]?.[0] || '';
      // Write the same value back (no-op, but requires edit rights)
      await this.callSheetsApi({
        worksheet,
        range,
        method: 'PUT',
        body: { values: [[currentValue]] },
        valueInputOption: 'USER_ENTERED',
      });
      return true;
    } catch (error) {
      if (error instanceof SheetsError && error.type === SheetsErrorType.PERMISSION_ERROR) {
        return false;
      }
      throw error;
    }
  }
  /**
   * Get all show results from the Show Results sheet (no filtering)
   * Columns: A Division, B Rider Name, C Horse Name, D Time, E ShowId
   */
  async getAllShowResults(): Promise<{
    rowIndexId: number;
    division: string;
    riderName: string;
    horseName: string;
    time: string;
    showId: string;
  }[]> {
    const worksheet = 'Show Results';
    const range = 'A2:E';
    const response = await this.callSheetsApi({ worksheet, range, usePublicSheet: true });
    const values: string[][] = response.values || [];
    return values.map((row, idx) => ({
      rowIndexId: idx + 1,
      division: row[0] || '',
      riderName: row[1] || '',
      horseName: row[2] || '',
      time: row[3] || '',
      showId: row[4] || '',
    }));
  }
  /**
   * Get all show results for a given showId from the Show Results sheet
   * Columns: A Division, B Rider Name, C Horse Name, D Time, E ShowId
   */
  async getShowResultsByShowId(showId: string): Promise<{
    rowIndexId: number;
    division: string;
    riderName: string;
    horseName: string;
    time: string;
    showId: string;
  }[]> {
    const worksheet = 'Show Results';
    const range = 'A2:E';
    const response = await this.callSheetsApi({ worksheet, range });
    const values: string[][] = response.values || [];
    const results = values
      .map((row, idx) => ({
        rowIndexId: idx + 1,
        division: row[0] || '',
        riderName: row[1] || '',
        horseName: row[2] || '',
        time: row[3] || '',
        showId: row[4] || '',
      }))
      .filter(r => r.showId === showId);
    return results;
  }

  /**
   * Add a new show result row to the Show Results sheet
   * Columns: A Division, B Rider Name, C Horse Name, D Time, E ShowId, F Show Name
   */
  async addShowResult(result: {
    division: string;
    riderName: string;
    horseName: string;
    time: string;
    showId: string;
    showName: string;
  }): Promise<void> {
    const worksheet = 'Show Results';
    const range = `${worksheet}!A:F`;
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${Config.EDITABLE_SHEET_ID}/values/${range}:append?valueInputOption=USER_ENTERED`;
    const values = [[
      result.division,
      result.riderName,
      result.horseName,
      result.time,
      result.showId,
      result.showName
    ]];
    const body = JSON.stringify({ values });
    let headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (!this.accessToken) {
      this.accessToken = await FirebaseAuthService.getAccessToken();
    }
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body
    });
    if (!response.ok) {
      await this.handleAPIError(response);
    }
    await response.json();
  }
      /**
       * Get the class order from column G of the Standings sheet
       */
      async getClassOrder(): Promise<string[]> {
        const worksheet = 'Standings';
        const range = 'G2:G';
        const response = await this.callSheetsApi({ worksheet, range });
        const values: string[][] = response.values || [];
        // Flatten and filter empty
        return values.map(row => row[0]).filter(Boolean);
      }

      /**
       * Update the class order in column G of the Standings sheet
       * Overwrites G2:G with the provided class list
       */
      async setClassOrder(classOrder: string[]): Promise<void> {
        const worksheet = 'Standings';
        const range = 'G2:G';
        const values = classOrder.map(c => [c]);
        await this.callSheetsApi({
          worksheet,
          range,
          method: 'PUT',
          body: { values },
          valueInputOption: 'USER_ENTERED',
        });
      }

      /**
       * Ensure a class exists in column G (append if missing)
       */
      async ensureClassInOrder(className: string): Promise<void> {
        const current = await this.getClassOrder();
        if (!current.includes(className)) {
          await this.setClassOrder([...current, className]);
        }
      }
    /**
     * Add a new standing row to the Standings sheet
     */
    async addStandingRow(standing: StandingRow): Promise<void> {
    const worksheet = 'Standings';
    const range = `${worksheet}!A:D`;
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${Config.EDITABLE_SHEET_ID}/values/${range}:append?valueInputOption=USER_ENTERED`;
    const values = [[
      standing.className,
      standing.riderName,
      standing.horseName,
      standing.points
    ]];
    const body = JSON.stringify({ values });
    let headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (!this.accessToken) {
      this.accessToken = await FirebaseAuthService.getAccessToken();
    }
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body
    });
    if (!response.ok) {
      await this.handleAPIError(response);
    }
    await response.json();
    }
  /**
   * Insert a new show row at a specific index in the Shows sheet
   */
  async insertShowRowAtIndex(show: Show, index: number): Promise<void> {
    const sheetName = 'Shows';
    const sheetId = await this.getSheetIdByName(sheetName);
    // 0-based index, header is row 0, so index 1 is first data row
    const requestBody = {
      requests: [
        {
          insertDimension: {
            range: {
              sheetId,
              dimension: 'ROWS',
              startIndex: index,
              endIndex: index + 1
            },
            inheritFromBefore: false
          }
        }
      ]
    };
    await this.callBatchUpdateApi(requestBody);

    // Now update the new row with show data
    const rowNumber = index + 1; // Sheets API is 1-based for values
    const range = `A${rowNumber}:H${rowNumber}`;
    const values = [[
      show.showName,
      show.showDateTime,
      show.streetAddress,
      show.cityState,
      show.addedMoney,
      show.canceled ? 'TRUE' : 'FALSE',
      show.showDetails,
      show.showId
    ]];
    await this.callSheetsApi({
      worksheet: sheetName,
      range,
      method: 'PUT',
      body: { values },
      valueInputOption: 'USER_ENTERED'
    });
    // Sort by showDateTime after insert
    await this.sortShowsByDateTime();
  }
  /**
   * Add a new show row to the Shows sheet
   */
  async addShowRow(show: Show): Promise<void> {
    const worksheet = 'Shows';
    const range = `${worksheet}!A:H`;
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${Config.EDITABLE_SHEET_ID}/values/${range}:append?valueInputOption=USER_ENTERED`;
    const values = [[
      show.showName,
      show.showDateTime,
      show.streetAddress,
      show.cityState,
      show.addedMoney,
      show.canceled ? 'TRUE' : 'FALSE',
      show.showDetails,
      show.showId
    ]];
    const body = JSON.stringify({ values });
    let headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (!this.accessToken) {
      this.accessToken = await FirebaseAuthService.getAccessToken();
    }
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body
    });
    if (!response.ok) {
      await this.handleAPIError(response);
    }
    return await response.json();
  }
  static TOTAL_SHOW_ROWS = 100;

    /**
     * Delete a show row by index in the Shows sheet
     */
    /**
     * Delete a row by index in the specified sheet
     */
    async deleteRowByIndex(sheetName: string, rowIndex: number): Promise<void> {
      // Google Sheets is 1-indexed, header is row 1, so rowIndex is correct
      // But batchUpdate uses 0-indexed, so subtract 1
      const sheetId = await this.getSheetIdByName(sheetName);
      const requestBody = {
        requests: [
          {
            deleteDimension: {
              range: {
                sheetId,
                dimension: 'ROWS',
                startIndex: rowIndex, // 0-indexed, header is row 0
                endIndex: rowIndex + 1
              }
            }
          }
        ]
      };
      await this.callBatchUpdateApi(requestBody);
    }

    // Helper to get sheetId from sheet name
    private async getSheetIdByName(sheetName: string): Promise<number> {
      const url = `https://sheets.googleapis.com/v4/spreadsheets/${Config.EDITABLE_SHEET_ID}`;
      const options: RequestInit = {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      };
      if (!this.accessToken) {
        this.accessToken = await FirebaseAuthService.getAccessToken();
      }
      if (this.accessToken) {
        (options.headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
      }
      const response = await fetch(url, options);
      const data = await response.json();
      const sheet = data.sheets.find((s: any) => s.properties.title === sheetName);
      if (!sheet) throw new SheetsError(SheetsErrorType.SHEET_NOT_FOUND, `Sheet ${sheetName} not found`);
      return sheet.properties.sheetId;
    }

    // Helper to call batchUpdate API
    private async callBatchUpdateApi(body: any): Promise<any> {
      const url = `https://sheets.googleapis.com/v4/spreadsheets/${Config.EDITABLE_SHEET_ID}:batchUpdate`;
      const options: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      };
      if (!this.accessToken) {
        this.accessToken = await FirebaseAuthService.getAccessToken();
      }
      if (this.accessToken) {
        (options.headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
      }
      const response = await fetch(url, options);
      if (!response.ok) {
        await this.handleAPIError(response);
      }
      return await response.json();
    }


  /**
   * Get all shows from the Shows sheet (A-G columns, up to TOTAL_SHOW_ROWS)
   */
  async getAllShows(): Promise<Show[]> {
    const worksheet = 'Shows';
    const range = `A1:H${GoogleSheetsService.TOTAL_SHOW_ROWS}`;
    const response = await this.callSheetsApi({ worksheet, range, usePublicSheet: true });
    const values: string[][] = response.values || [];
    const shows: Show[] = [];
    values.forEach((show, index) => {
      if (index === 0) return; // Skip header row
      if (show[0]?.trim() !== '') {
        let formattedDate = show[1] || '';
        shows.push({
          rowIndexId: index,
          showName: show[0] || '',
          showDateTime: formattedDate,
          streetAddress: show[2] || '',
          cityState: show[3] || '',
          addedMoney: show[4] || '',
          canceled: (show[5] || '').toUpperCase() === 'TRUE',
          showDetails: show[6] || '',
          showId: show[7] || '',
        });
      }
    });
    return shows;
  }

  /**
   * Get all standings from the Standings sheet (A2:D)
   */
  async getAllStandings(): Promise<StandingRow[]> {
    const worksheet = 'Standings';
    const range = 'A2:D';
    const response = await this.callSheetsApi({ worksheet, range });
    const values: string[][] = response.values || [];
    const standings: StandingRow[] = [];
    values.forEach((row, index) => {
      if (row[0]?.trim() !== '') {
        standings.push({
          rowIndexId: index + 1, // A2 is index 1
          className: row[0] || '',
          riderName: row[1] || '',
          horseName: row[2] || '',
          points: Number(row[3]) || 0,
        });
      }
    });
    return standings;
  }

    /**
     * Update a standing row by index in the Standings sheet
     */
    async updateStandingRowByIndex(rowIndex: number, standing: StandingRow): Promise<void> {
      const worksheet = 'Standings';
      // Google Sheets is 1-indexed, header is row 1, so rowIndex is correct (A2 is index 1)
      const range = `A${rowIndex+1}:D${rowIndex+1}`;
      const values = [[
        standing.className,
        standing.riderName,
        standing.horseName,
        standing.points
      ]];
      await this.callSheetsApi({
        worksheet,
        range,
        method: 'PUT',
        body: { values },
        valueInputOption: 'USER_ENTERED'
      });
    }
  /**
   * Update a show row by index in the Shows sheet
   */

  async updateShowRowByIndex(rowIndex: number, show: Show): Promise<void> {
    const worksheet = 'Shows';
    // Google Sheets is 1-indexed, header is row 1, so rowIndex is correct
    const range = `A${rowIndex+1}:H${rowIndex+1}`;
    const values = [[
      show.showName,
      show.showDateTime,
      show.streetAddress,
      show.cityState,
      show.addedMoney,
      show.canceled ? 'TRUE' : 'FALSE',
      show.showDetails,
      show.showId
    ]];
    await this.callSheetsApi({
      worksheet,
      range,
      method: 'PUT',
      body: { values },
      valueInputOption: 'USER_ENTERED'
    });
    // Sort by showDateTime after update
    await this.sortShowsByDateTime();
  }


  /**
   * Common function to handle Sheets API calls (URL, auth, error handling)
   */
  private async callSheetsApi({
    worksheet,
    range,
    method = 'GET',
    body,
    valueInputOption,
    usePublicSheet = false,
  }: {
    worksheet: string;
    range: string;
    method?: string;
    body?: any;
    valueInputOption?: string;
    usePublicSheet?: boolean;
  }): Promise<any> {
    try {
      // Use public sheet for GET/read-only requests, editable for write
      const sheetId = (method === 'GET' || usePublicSheet)
        ? Config.SHEETS_URL.split('/').pop() // extract ID from URL
        : Config.EDITABLE_SHEET_ID;
      let url = `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${worksheet}!${range}`;
      let hasQuery = false;
      if (valueInputOption) {
        url += `?valueInputOption=${valueInputOption}`;
        hasQuery = true;
      }
      // Always add API key for GET/public requests
      if (method === 'GET') {
        url += (hasQuery ? '&' : '?') + `key=${Config.SHEETS_KEY}`;
      }
      const options: RequestInit = {
        method,
        headers: { 'Content-Type': 'application/json' },
      };
      if (body) {
        options.body = JSON.stringify(body);
      }

      // Only require auth for non-GET (write) requests
      if (method !== 'GET') {
        if (!this.accessToken) {
          this.accessToken = await FirebaseAuthService.getAccessToken();
        }
        if (this.accessToken) {
          (options.headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
        } else {
          throw new SheetsError(
            SheetsErrorType.AUTHENTICATION_ERROR,
            'No valid authentication token available. Please sign in again.'
          );
        }
      }
      const response = await fetch(url, options);
      if (!response.ok) {
        await this.handleAPIError(response);
      }
      return await response.json();
    } catch (error) {
      if (error instanceof SheetsError) throw error;
      throw new SheetsError(SheetsErrorType.UNKNOWN_ERROR, 'Sheets API call failed', error);
    }
  }

  private accessToken: string | null;

  constructor(accessToken?: string) {
    this.accessToken = accessToken || null;
  }

  /**
   * Create a GoogleSheetsService instance from a Firebase user
   */
  static async fromFirebaseUser(user: firebase.User): Promise<GoogleSheetsService | null> {
    try {
      // Try to get the stored OAuth access token
      const accessToken = await FirebaseAuthService.getAccessToken();
      
      if (accessToken) {
        console.log('Using OAuth access token for Sheets API');
        return new GoogleSheetsService(accessToken);
      } else {
        console.log('No OAuth access token available, using API key for read-only access');
        return new GoogleSheetsService(); // No token, read-only mode
      }
    } catch (error) {
      console.error('Error creating GoogleSheetsService:', error);
      return new GoogleSheetsService(); // Fallback to read-only mode
    }
  }

  /**
   * Handle API errors with specific error types and retry logic
   */
  private async handleAPIError(
    response: Response,
  ): Promise<void> {
    const status = response.status;
    let errorData: any = {};
    
    try {
      errorData = await response.json();
    } catch {
      // If we can't parse the error response, continue with empty error data
    }

    switch (status) {
      case 401:
        // Unauthorized - token might be expired or invalid
        throw new SheetsError(
          SheetsErrorType.AUTHENTICATION_ERROR,
          'Authentication failed. Please sign in again.',
          errorData
        );

      case 403:
        // Forbidden - check if it's a permission issue or rate limiting
        if (errorData.error?.code === 403 && errorData.error?.status === 'PERMISSION_DENIED') {
          throw new SheetsError(
            SheetsErrorType.PERMISSION_ERROR,
            'You do not have permission to access this Google Sheet. Please ensure the sheet is shared with your account.',
            errorData
          );
        } else if (errorData.error?.message?.includes('rate limit') || 
                   errorData.error?.message?.includes('quota')) {
          throw new SheetsError(
            SheetsErrorType.RATE_LIMIT_ERROR,
            'Rate limit exceeded. Please try again later.',
            errorData
          );
        }
        
        throw new SheetsError(
          SheetsErrorType.PERMISSION_ERROR,
          'Access forbidden. Please check your permissions.',
          errorData
        );

      case 404:
        throw new SheetsError(
          SheetsErrorType.SHEET_NOT_FOUND,
          'Google Sheet not found. Please check the sheet ID and worksheet name in your configuration.',
          errorData
        );

      case 400:
        if (errorData.error?.message?.includes('range')) {
          throw new SheetsError(
            SheetsErrorType.INVALID_RANGE,
            'Invalid range specified. Please check your worksheet name and range.',
            errorData
          );
        }
        
        throw new SheetsError(
          SheetsErrorType.UNKNOWN_ERROR,
          `Bad request: ${errorData.error?.message || response.statusText}`,
          errorData
        );

      case 429:
        
        throw new SheetsError(
          SheetsErrorType.RATE_LIMIT_ERROR,
          'Too many requests. Please try again later.',
          errorData
        );

      case 500:
      case 502:
      case 503:
      case 504:
        throw new SheetsError(
          SheetsErrorType.UNKNOWN_ERROR,
          `Server error (${status}). Please try again later.`,
          errorData
        );

      default:
        throw new SheetsError(
          SheetsErrorType.UNKNOWN_ERROR,
          `Unexpected error (${status}): ${response.statusText}`,
          errorData
        );
    }
  }

  /**
   * Utility function for delays
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Sort the Shows sheet by column B (showDateTime)
   */
  async sortShowsByDateTime(): Promise<void> {
    const sheetId = await this.getSheetIdByName('Shows');
    const requestBody = {
      requests: [
        {
          sortRange: {
            range: {
              sheetId,
              startRowIndex: 1, // skip header
              startColumnIndex: 0,
              endColumnIndex: 8 // A-H
            },
            sortSpecs: [
              {
                dimensionIndex: 1, // column B (showDateTime)
                sortOrder: 'ASCENDING'
              }
            ]
          }
        }
      ]
    };
    await this.callBatchUpdateApi(requestBody);
  }
}
